import { matchesSelector } from '../../dist/js/functions.min.js';

(() => {
	//
	const rootSelector        = '.elementary-decision-tree';
	const nodeSelector        = '.node'
	const nodeLinkSelector    = '.node__child-link'
	const breadcrumbsSelector = '.elementary-decision-tree__breadcrumbs';
	const breadcrumbSelector  = '.elementary-decision-tree__breadcrumb';

	//
	class BlockHandler {
		#rootEl;

		/****/
		constructor(rootEl) {
			this.#rootEl = rootEl;

			// Watch for the user clicking child node links
			this.#rootEl.addEventListener('click', this.#handleNodeLinkClick.bind(this));

			// Watch for the user clicking breadcrumb links
			this.#rootEl.addEventListener('click', this.#handleBreadcrumbClick.bind(this));

			// Listen for history pop events
			window.addEventListener('popstate', this.#handleHistoryPop.bind(this));

			// Set the initial state of the breadcrumbs
			this.#rebuildBreadcrumbs();
		}

		/**
		 * Handle the user clicking child node links 
		 */
		#handleNodeLinkClick(event) {
			// Verify the event target
			const targetEl = event.target;
			if (!(targetEl instanceof HTMLElement)) return;
			if (!matchesSelector(targetEl, `${nodeLinkSelector}[data-name]`)) return;

			//
			event.preventDefault();

			// Find the link el
			const linkEl = targetEl.closest(nodeLinkSelector);
			if (!(linkEl instanceof HTMLElement)) return;

			// Get values from the link element
			const targetNodeName = String(linkEl.dataset.name ?? '');

			//
			this.#updatePageUrl(targetNodeName);
			this.#activateNode(targetNodeName);
			this.#rebuildBreadcrumbs();
		}

		/**
		 * Handle the user clicking breadcrumb links
		 */
		#handleBreadcrumbClick(event) {
			// Verify the event target
			const targetEl = event.target;
			if (!(targetEl instanceof HTMLElement)) return;
			if (!matchesSelector(targetEl, breadcrumbSelector)) return;

			//
			event.preventDefault();

			// Find the link el
			const breadcrumbEl = targetEl.closest(breadcrumbSelector);
			if (!(breadcrumbEl instanceof HTMLElement)) return;

			// Get values from the link element
			const targetNodeName = String(breadcrumbEl.dataset.name ?? '');

			//
			this.#updatePageUrl(targetNodeName);
			this.#activateNode(targetNodeName);
			this.#rebuildBreadcrumbs();
		}

		/**
		 * Push the new active node URL into the browser history 
		 */
		#updatePageUrl(targetNodeName) {
			const url = new URL(location.href);
			url.searchParams.set('node', targetNodeName);
			window.history.pushState(null, null, url.toString());
		}

		/**
		 * Change and reveal the active node 
		 */
		#activateNode(targetName) {
			// Activate/Deactivate all nodes
			const allNodeEls = [...this.#rootEl.querySelectorAll(nodeSelector)];
			allNodeEls.forEach(nodeEl => {
				const nodeName = String(nodeEl.dataset.name ?? '');
				const isActive = nodeName === targetName;
				nodeEl.dataset.active = (isActive) ? '1' : '0';
			});

			// Update the referer of any contact nodes (so that Schuco can see which node the lead came from)
			const inputEls = [...this.#rootEl.querySelectorAll(`${nodeSelector}[data-type="contact"] input[name="_wp_http_referer"]`)];
			const pathAndQuery = window.location.pathname + window.location.search;
			inputEls.forEach(el => el.setAttribute('value', pathAndQuery));
		}

		/**
		 * Rebuild the breadcrumbs to reflect the current active node 
		 */
		#rebuildBreadcrumbs() {
			//  Find required elements
			const breadcrumbsEl = this.#rootEl.querySelector(breadcrumbsSelector);
			const activeNodeEl = this.#rootEl.querySelector(`${nodeSelector}[data-active="1"]`);
			if (
				!(breadcrumbsEl instanceof HTMLElement) ||
				!(activeNodeEl instanceof HTMLElement)
			) return;

			// Search up from the current active node to create a trail of breadcrumbs
			const breadcrumbsData = [];
			let currentNodeId = parseInt(activeNodeEl.dataset.id ?? 0, 10);
			let parentNodeId = parseInt(activeNodeEl.dataset.parentId ?? 0, 10);
			while (0 < parentNodeId) {
				const currentNodeEl = this.#rootEl.querySelector(`${nodeSelector}[data-id="${parentNodeId}"]`);
				const nodeLinkEl = currentNodeEl?.querySelector(`${nodeLinkSelector}[data-id="${currentNodeId}"]`);
				if (
					!(currentNodeEl instanceof HTMLElement) ||
					!(nodeLinkEl instanceof HTMLElement)
				) break;;

				const nodeTextContent = String(nodeLinkEl.textContent);
				const currentNodeName = String(currentNodeEl.dataset.name ?? '');
				currentNodeId = parseInt(currentNodeEl.dataset.id ?? 0, 10);
				parentNodeId = parseInt(currentNodeEl.dataset.parentId ?? 0, 10);

				// Store breadcrumb data
				breadcrumbsData.unshift({
					nodeName: currentNodeName,
					label: nodeTextContent,
				});
			}

			// Remove extra existing breadcrumbs
			while (breadcrumbsEl.children.length > breadcrumbsData.length) {
				breadcrumbsEl.lastElementChild.remove();
			}

			// Find the breadcrumbs template
			const templateEl = this.#rootEl.querySelector(`template[data-type="breadcrumb"]`);
			if (!(templateEl instanceof HTMLTemplateElement)) return;

			// For each entry in the breadcrumbs data
			for (let i = 0; i < breadcrumbsData.length; i++) {
				// Find or create a breadcrumb
				let breadcrumbEl = breadcrumbsEl.children[i];
				if (!(breadcrumbEl instanceof HTMLElement)) {
					breadcrumbEl = templateEl.content.cloneNode(true);
					breadcrumbsEl.append(breadcrumbEl);
					breadcrumbEl = breadcrumbsEl.children[i];
				}
	
				// Set the node ID
				breadcrumbEl.dataset.name = breadcrumbsData[i].nodeName;

				// Set the label
				const labelEl = breadcrumbEl.querySelector('.breadcrumb__label');
				if (labelEl instanceof HTMLElement) {
					labelEl.textContent = String(breadcrumbsData[i].label);
				}
			}

			// Show/Hide the breadcrumbs
			breadcrumbsEl.dataset.haveEntries = (0 === breadcrumbsEl.children.length) ? '0' : '1';
		}

		/**
		 * Handle the back button being clicked 
		 */
		#handleHistoryPop() {
			// Get the new URL params
			const searchParams = new URLSearchParams(window.location.search);
	
			// Find the active node name
			let targetNodeName = searchParams.get('node') ?? '';
			if (0 === targetNodeName.length) {
				targetNodeName = String(this.#rootEl.dataset.rootNodeName ?? '');
			}

			//
			this.#activateNode(targetNodeName);
			this.#rebuildBreadcrumbs();
		}
	}

	//
	const rootEls = [...document.querySelectorAll(rootSelector)];
	rootEls.forEach(rootEl => new BlockHandler(rootEl));

})();
